import React, { useEffect, useState } from 'react';
import Slider from 'react-slick';
import './Slider.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import ProductInfoCard from '../ProductInfoCard/ProductInfoCard';

function ProductCarousel({ category, products: initialProducts = [], displayName }) {
  const [products, setProducts] = useState(initialProducts);
  const [displayProducts, setDisplayProducts] = useState([]);
  const [categoryDisplayName, setCategoryDisplayName] = useState(displayName || '');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [hoveredProductId, setHoveredProductId] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

  const isTouchDevice = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  useEffect(() => {
    const fetchCategoryDetails = async () => {
      try {
        const response = await fetch(`${backendUrl}/category/details/${category}`);
        const data = await response.json();
        if (data.success) {
          setCategoryDisplayName(data.category.displayName);
        } else {
          console.error('Error fetching category details:', data.message);
        }
      } catch (err) {
        console.error('Error fetching category details:', err);
      }
    };

    const fetchProducts = async () => {
      try {
        const response = await fetch(`${backendUrl}/product/category/${category}`);
        const data = await response.json();
        if (data.success) {
          setProducts(data.products);
        } else {
          console.error('Error fetching products:', data.message);
        }
      } catch (err) {
        console.error('Error fetching products:', err);
      }
    };

    // Only fetch if not provided
    if (initialProducts.length === 0) {
      fetchProducts();
    }
    if (!displayName) {
      fetchCategoryDetails();
    }
  }, [category, backendUrl, initialProducts.length, displayName]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getSlidesToShow = () => {
    if (windowWidth >= 1200) return 5;
    if (windowWidth >= 992) return 4;
    if (windowWidth >= 768) return 3;
    if (windowWidth >= 576) return 2;
    return 2;
  };

  useEffect(() => {
    const slidesToShow = getSlidesToShow();
    let temp = [...products];
    if (products.length > 0 && products.length < slidesToShow) {
      while (temp.length < slidesToShow) {
        temp = temp.concat(products);
      }
      temp = temp.slice(0, slidesToShow);
    }
    setDisplayProducts(temp);
  }, [products, windowWidth]);

  const handleProductClick = (product) => {
    if (isDragging) return;
    setSelectedProduct(product);
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setSelectedProduct(null);
  };

  if (products.length === 0) return null;

  const settings = {
    key: windowWidth,
    infinite: true,
    speed: 500,
    slidesToShow: getSlidesToShow(),
    slidesToScroll: getSlidesToShow(),
    autoplay: false,
    arrows: true,
    nextArrow: <div className="slick-next">→</div>,
    prevArrow: <div className="slick-prev">←</div>,
    beforeChange: () => setIsDragging(true),
    afterChange: () => setTimeout(() => setIsDragging(false), 50),
  };

  return (
    <div className="product-carousel-container">
      <h2 className="slider-title">{categoryDisplayName}</h2>
      <Slider {...settings}>
        {displayProducts.map((product, index) => {
          const uniqueKey = `${product._id}-${index}`;
          const isHovered = hoveredProductId === uniqueKey;
          const hasVideo = !!product.featuredVideo;

          return (
            <div
              key={uniqueKey}
              className="carousel-slide"
              onClick={() => handleProductClick(product)}
              onMouseEnter={() => !isTouchDevice() && setHoveredProductId(uniqueKey)}
              onMouseLeave={() => !isTouchDevice() && setHoveredProductId(null)}
              style={{ position: 'relative' }}
            >
              <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                <img
                  src={product.featuredImage}
                  alt={product.title}
                  className="product-image base-image"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '10px',
                  }}
                />
                {!isTouchDevice() && hasVideo && (
                  <video
                    src={product.featuredVideo}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="product-image hover-video"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '10px',
                      opacity: isHovered ? 1 : 0,
                      transition: 'opacity 0.5s ease-in-out',
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </Slider>

      {selectedProduct && (
        <ProductInfoCard
          product={selectedProduct}
          handleClosePopup={handleClosePopup}
          showPopup={showPopup}
        />
      )}
    </div>
  );
}

export default ProductCarousel;
